import * as fs from 'fs';
import * as path from 'path';
import { colors } from './const';

const c = colors;

const OK_MAX = 150;
const WARN_MAX = 250;

const SLOW_HINTS: Record<string, string> = {
  'MANIFEST.json': 'skim MANIFEST.routing.json instead',
  '00-integration/agent-per-type-verification.md': 'mandatory fast-path gate — read Data/Logic/SDK rows for §3b types only',
  '00-integration/layout-cookbook.md': 'card-per-type reference — read only your fetched types\' cards, not the whole file',
  '00-integration/reverification-hooks.md': 'read only the HOOK section for your current moment + audit template at HOOK 3',
  '00-integration/phase-5-6-build-gate.md': 'read build-gate/verify-core.md only',
  '00-integration/mockup-to-config-guide.md': 'read mockup/index.md + mockup-styling-paths.json',
  '00-integration/agent-execution-flow.md': 'read execution-flow/index.md on fast path skip',
  '00-integration/agent-operating-contract.md': 'read operating-contract/index.md on fast path skip',
  '00-integration/aws-deploy.md': 'INPUT 3 deploy only — read deploy/aws/index.md',
};

const MANDATORY_FAST_PATH_READS = new Set([
  '00-integration/agent-per-type-verification.md',
  '00-integration/layout-cookbook.md',
]);

function normalizeRelPath(rel: string): string {
  return rel.replace(/^\.\.\//, '');
}

export type DocTier = 'ok' | 'warn' | 'slow' | 'missing';

export interface DocAuditEntry {
  relPath: string;
  lines: number;
  tier: DocTier;
  hint?: string;
}

export interface DocReadAuditResult {
  profile: string;
  entries: DocAuditEntry[];
  slow: DocAuditEntry[];
  totalLines: number;
  ok: boolean;
}

export function getSdkDocsRoot(): string {
  return path.join(__dirname, '..', '..', 'templates', 'docs');
}

function tierForLines(lines: number): DocTier {
  if (lines <= OK_MAX) return 'ok';
  if (lines <= WARN_MAX) return 'warn';
  return 'slow';
}

function resolveDocPath(docsRoot: string, rel: string): string | null {
  const normalized = rel.split('#')[0]!.split('?')[0]!;
  if (normalized.includes('*')) return null;

  const candidates = [
    path.join(docsRoot, normalized),
    path.join(docsRoot, '..', normalized),
  ];

  for (const abs of candidates) {
    if (fs.existsSync(abs) && fs.statSync(abs).isFile()) return abs;
  }
  return null;
}

function countLines(filePath: string): number {
  return fs.readFileSync(filePath, 'utf8').split('\n').length;
}

function collectManifestPaths(manifest: Record<string, unknown>, profile: string, phaseId?: string): string[] {
  const paths = new Set<string>();

  if (profile === 'fastPath') {
    const fp = manifest.fastPath as { read?: string[]; readWhenMockup?: string[] } | undefined;
    for (const p of fp?.read ?? []) paths.add(p);
  } else if (profile === 'phase' && phaseId !== undefined) {
    const phases = manifest.phases as Array<Record<string, unknown>> | undefined;
    const phase = phases?.find((p) => String(p.id) === String(phaseId));
    if (phase) {
      for (const key of Object.keys(phase)) {
        if (!key.startsWith('read')) continue;
        const val = phase[key];
        if (Array.isArray(val)) {
          for (const p of val) if (typeof p === 'string') paths.add(p);
        }
      }
    }
  } else if (profile === 'perType') {
    const qt = manifest.questionTypes as Record<string, Record<string, string>> | undefined;
    for (const entry of Object.values(qt ?? {})) {
      for (const field of ['spineSlice', 'blueprint', 'reference', 'uiSpec'] as const) {
        const p = entry[field];
        if (typeof p === 'string') paths.add(p);
      }
    }
  }

  return [...paths];
}

function hintFor(relPath: string): string | undefined {
  const key = relPath.replace(/^\.\.\//, '');
  return SLOW_HINTS[key] ?? SLOW_HINTS[relPath];
}

export function auditDocPaths(
  docsRoot: string,
  relPaths: string[],
  profileLabel: string,
): DocReadAuditResult {
  const entries: DocAuditEntry[] = [];

  for (const rel of relPaths) {
    const abs = resolveDocPath(docsRoot, rel);
    if (!abs) {
      entries.push({ relPath: rel, lines: 0, tier: 'missing' });
      continue;
    }
    const lines = countLines(abs);
    const tier = tierForLines(lines);
    const hint = tier === 'slow' || tier === 'warn' ? hintFor(rel.replace(/^\.\.\//, '')) : undefined;
    entries.push({ relPath: rel, lines, tier, hint });
  }

  entries.sort((a, b) => b.lines - a.lines);
  const slow = entries.filter((e) => e.tier === 'slow');
  const blockingSlow = slow.filter((e) => !MANDATORY_FAST_PATH_READS.has(normalizeRelPath(e.relPath)));
  const totalLines = entries.reduce((sum, e) => sum + e.lines, 0);

  return {
    profile: profileLabel,
    entries,
    slow,
    totalLines,
    ok: blockingSlow.length === 0,
  };
}

export function loadManifest(docsRoot: string): Record<string, unknown> {
  const manifestPath = path.join(docsRoot, 'MANIFEST.json');
  return JSON.parse(fs.readFileSync(manifestPath, 'utf8')) as Record<string, unknown>;
}

export function runDocReadAudit(options: {
  docsRoot?: string;
  profile?: 'fastPath' | 'phase' | 'perType';
  phaseId?: string;
  warnOnly?: boolean;
  ci?: boolean;
  writeInventory?: boolean;
}): number {
  const docsRoot = options.docsRoot ?? getSdkDocsRoot();
  const profile = options.profile ?? 'fastPath';
  const manifest = loadManifest(docsRoot);

  const relPaths =
    profile === 'phase'
      ? collectManifestPaths(manifest, 'phase', options.phaseId)
      : profile === 'perType'
        ? collectManifestPaths(manifest, 'perType')
        : collectManifestPaths(manifest, 'fastPath');

  const label =
    profile === 'phase' ? `phase ${options.phaseId ?? '?'}` : profile === 'perType' ? 'perType' : 'fastPath';
  const result = auditDocPaths(docsRoot, relPaths, label);

  console.log(`\n${c.bold}Doc read budget audit (${result.profile})${c.reset}`);
  console.log('─'.repeat(40));

  for (const entry of result.entries) {
    const tag =
      entry.tier === 'ok'
        ? `${c.green}OK  ${c.reset}`
        : entry.tier === 'warn'
          ? `${c.yellow}WARN${c.reset}`
          : entry.tier === 'slow'
            ? `${c.red}SLOW${c.reset}`
            : `${c.red}MISS${c.reset}`;
    const hint = entry.hint ? ` — ${entry.hint}` : '';
    const lineLabel = entry.tier === 'missing' ? 'missing' : `${entry.lines} lines`;
    console.log(`${tag} ${entry.relPath.padEnd(42)} ${lineLabel}${hint}`);
  }

  console.log(`\n${c.dim}Total: ${result.totalLines} lines across ${result.entries.length} paths${c.reset}`);

  if (result.slow.length > 0) {
    console.log(`\n${c.yellow}A few doc reads are taking longer than expected even from temp files:${c.reset}`);
    for (const entry of result.slow) {
      const hint = entry.hint ? ` — ${entry.hint}` : '';
      console.log(`  - ${entry.relPath} (${entry.lines} lines)${hint}`);
    }
  }

  if (options.writeInventory) {
    const inventory: Record<string, { lines: number; tier: DocTier }> = {};
    for (const entry of result.entries) {
      if (entry.tier !== 'missing') {
        inventory[entry.relPath] = { lines: entry.lines, tier: entry.tier };
      }
    }
    manifest.docInventory = {
      generatedBy: 'npx cfm-sdk doc-read-audit --write',
      caps: { ok: OK_MAX, warn: WARN_MAX },
      profile: result.profile,
      files: inventory,
    };
    fs.writeFileSync(
      path.join(docsRoot, 'MANIFEST.json'),
      `${JSON.stringify(manifest, null, 2)}\n`,
      'utf8',
    );
    console.log(`\n${c.green}OK:${c.reset} Wrote docInventory to MANIFEST.json`);
  }

  if (result.ok || options.warnOnly) {
    return 0;
  }
  if (options.ci) {
    return 1;
  }
  return result.slow.length > 0 ? 1 : 0;
}

export function formatDocBudgetHint(docsRoot?: string): string {
  const root = docsRoot ?? getSdkDocsRoot();
  const manifest = loadManifest(root);
  const relPaths = collectManifestPaths(manifest, 'fastPath');
  const result = auditDocPaths(root, relPaths, 'fastPath');
  return `Doc budget: npx cfm-sdk doc-read-audit — fast path reads ${result.entries.length} files (~${result.totalLines} lines)`;
}
